import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Aviso } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { Icono, type NombreIcono } from './Iconos.js';

interface Props {
  idioma: Idioma;
  onAbrir: (aviso: Aviso) => void;
}

const ICONO: Record<string, NombreIcono> = {
  INCIDENCIA_CRITICA: 'aviso',
  VISITA_VENCIDA: 'calendario',
  MANTENIMIENTO_VENCIDO: 'llave-inglesa',
  GASTO_PENDIENTE: 'euro',
  PAGO_RECURRENTE: 'repetir',
  FACTURA_VENCIDA: 'factura',
  OTRO: 'chispa',
};

/**
 * Campana de avisos. Los avisos se derivan del estado real, así que basta con
 * volver a pedirlos: no hay una bandeja que pueda quedarse desincronizada.
 */
/** Lo que mide el panel. Tiene que coincidir con el ancho de la hoja de estilo. */
const ANCHO_PANEL = 352;
const MARGEN = 16;

export function Avisos({ idioma, onAbrir }: Props) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const campana = useRef<HTMLButtonElement>(null);
  /*
    EL PANEL SALE DE LA BARRA LATERAL, Y LA BARRA LO RECORTABA.
    Estaba colocado en `absolute` dentro del lateral, que lleva
    `overflow: hidden` para no desbordar la pantalla. Resultado: del panel solo
    se veia la tira de cuatro centimetros que cabia entre la campana y el borde
    de la barra; el resto —los avisos, el boton de marcarlos— quedaba cortado.

    Con `fixed` sale del recorte, pero entonces hay que decirle donde ponerse:
    se mide la campana al abrir, y ahi se ancla. Asi el panel sigue perteneciendo
    a su boton en vez de aparecer en una esquina cualquiera.
  */
  const [sitio, setSitio] = useState<{ left: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    if (!abierto) return undefined;
    const colocar = () => {
      const caja = campana.current?.getBoundingClientRect();
      if (!caja) return;
      const ancho = Math.min(ANCHO_PANEL, window.innerWidth - MARGEN * 2);
      setSitio({
        // Pegado a la campana, pero sin salirse por la derecha de la ventana.
        left: Math.max(MARGEN, Math.min(caja.left, window.innerWidth - ancho - MARGEN)),
        // Se abre hacia arriba: el boton vive abajo del todo.
        bottom: window.innerHeight - caja.top + 8,
      });
    };
    colocar();
    window.addEventListener('resize', colocar);
    // En captura, para enterarse tambien del desplazamiento de un panel interior.
    window.addEventListener('scroll', colocar, true);
    return () => {
      window.removeEventListener('resize', colocar);
      window.removeEventListener('scroll', colocar, true);
    };
  }, [abierto]);

  const cargar = () => {
    api
      .avisos()
      .then((r) => setAvisos(r.avisos))
      .catch(() => undefined);
  };

  useEffect(() => {
    cargar();
    // Se refresca cada pocos minutos: no es un chat, no necesita tiempo real.
    const id = setInterval(cargar, 180_000);
    return () => clearInterval(id);
  }, []);

  // Cerrar al pulsar fuera: un panel que no se cierra tapa la aplicación.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const sinLeer = avisos.filter((a) => !a.leido).length;

  return (
    <div className="avisos" ref={contenedor}>
      <button
        ref={campana}
        type="button"
        className="avisos-campana"
        aria-expanded={abierto}
        aria-label={t(idioma, 'avisos.titulo')}
        onClick={() => setAbierto((v) => !v)}
      >
        <Icono nombre="alerta" />
        {sinLeer > 0 && <span className="insignia">{sinLeer > 9 ? '9+' : sinLeer}</span>}
      </button>

      {abierto && sitio && (
        <div
          className="avisos-panel"
          role="dialog"
          aria-label={t(idioma, 'avisos.titulo')}
          style={{ left: `${sitio.left}px`, bottom: `${sitio.bottom}px` }}
        >
          <div className="avisos-cabeza">
            <b>{t(idioma, 'avisos.titulo')}</b>
            {sinLeer > 0 && (
              <button
                type="button"
                className="enlace"
                onClick={() => void api.marcarAvisosLeidos().then(cargar)}
              >
                ✓ {t(idioma, 'avisos.marcarTodas')}
              </button>
            )}
          </div>

          {avisos.length === 0 && (
            <p className="suave" style={{ padding: '0.9rem' }}>
              {t(idioma, 'avisos.vacio')}
            </p>
          )}

          <div className="avisos-lista">
            {avisos.slice(0, 40).map((a) => (
              <button
                key={a.id}
                type="button"
                className={`aviso${a.leido ? ' leido' : ''}`}
                onClick={() => {
                  void api.marcarAvisoLeido(a.entidadId).then(cargar);
                  setAbierto(false);
                  onAbrir(a);
                }}
              >
                <span className="aviso-icono" aria-hidden="true">
                  <Icono nombre={ICONO[a.tipo] ?? 'chispa'} />
                </span>
                <span className="crecer">
                  <b>{a.titulo}</b>
                  <small>
                    {t(idioma, `avisoTipo.${a.tipo}`)}
                    {a.detalle ? ` · ${a.detalle}` : ''}
                  </small>
                </span>
                {!a.leido && <span className="punto-rojo" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
